import { useEffect } from 'react'
import { Location } from 'history'
import { useHistory } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import { capitalizeFirst } from '@sketch/utils'

interface UseSocialErrorHandlerProps {
  location: Location
}

const DEFAULT_MESSAGE = 'Something went wrong'

const SOCIAL_ERROR_MESSAGES: Record<string, string> = {
  userCancel: 'Sign in with :provider cancelled, please try again.',
  deleteAccount: 'Account deletion with :provider failed, please try again',
}

/**
 *
 * During the Social provider sign-in flow, if an error occurs (e.g., user cancels the sign-in process),
 * the user is redirected back to the sign-in page with error details in the URL parameters.
 * This hook checks for these error parameters, displays an appropriate toast notification,
 * and then cleans up the URL to remove the error parameters.
 *
 * @param location - The current location object from react-router.
 */
export const useSocialErrorHandler = ({
  location,
}: UseSocialErrorHandlerProps) => {
  const { showToast } = useToast()
  const history = useHistory()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const errorParam = urlParams.get('error')
    const provider = urlParams.get('authp') // Auth provider (e.g., apple, google, ...)

    if (errorParam && SOCIAL_ERROR_MESSAGES[errorParam]) {
      const errorMessage = SOCIAL_ERROR_MESSAGES[errorParam]
      const hasProviderPlaceholder =
        errorMessage.includes(':provider') && provider

      const formattedMessage = hasProviderPlaceholder
        ? errorMessage.replace(':provider', capitalizeFirst(provider))
        : DEFAULT_MESSAGE

      showToast(formattedMessage, 'negative')

      // Clear all URL parameters
      history.replace(location.pathname)
    }
  }, [location.search, location.pathname, showToast, history])
}
